import "./PaymentVoucherSetupStyle.css";

import * as yup from "yup";

import { AccountantService, ClientService } from "../../services";
import {
	BackdropCircularProgress,
	DeleteConfirmationDialog,
	EnhancedButtonPrimeReactSeverity,
	EnhancedDialog,
	EnhancedSnackbar,
	SingleColumnLayout,
	StandardPanel,
	TwoColumnLayout,
} from "@websential/cosmic-v20";
import { Box, Button, Card, Grid, Typography, useTheme } from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import {
	IGetListRequest,
	IResponse,
	IServerError,
} from "../../models/common";
import {
	INITIAL_PAGE_SIZE,
	INITIAL_SNACKBAR_DATA,
	ROWS_PER_PAGE_PER_OPTIONS,
} from "../../common/constants";
import { Link, useNavigate, useParams } from "react-router-dom";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
	formatDate,
	scrollPanelFieldsToTop,
	sortDDLByLabel,
	useNavigateToLogout,
} from "../../common/Common";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { BasicSetupFormToolbar } from "../BasicSetupFormToolbar";
import { CountryService } from "../../services/common/CountryService";
import { DDLDataTable } from "../DDLDataTable";
import { EmployeeService } from "../../services/EmployeeService";
import { EnhancedButtonPrimeReact } from "../EnhancedButtonPrimeReact";
import { IAppState } from "../../store/reducers";
import { IDropDownListResponse } from "../../models/common/IDropDownListResponse";
import { IEmployee } from "../../models";
import { IEmployeeBank } from "../../models/Employee/EmployeeBank";
import { PaymentVoucherSetupFeildsPanel } from "./Panels/PaymentVoucherSetupFeildsPanel";
import { PaymentVoucherSetupRightTablePanel } from "./Panels/PaymentVoucherSetupRightTablePanel";
import { PaymentVoucherSetupToolbar } from "./PaymentVoucherSetupToolbar/PaymentVoucherSetupToolbar";
import { PaymentVoucherSetupValidationSchema } from "./validator";
import { TransactionsServices } from "../../services/DirectDeposit/TransactionsServices";
import { setSelectedDrawerOptionsMenu } from "../../store/actions/drawerMenuOptionsActions";
import { setSelectedStickyNavBar } from "../../store/actions/stickyNavBarActions";
import { yupResolver } from "@hookform/resolvers/yup";

export interface IPaymentVoucherSetup { }

// ─── Constants ────────────────────────────────────────────────────────────────

const SINGLE_RECORD_DATA_QUERY_KEY = "payment-voucher-single-data-record";
const PAYMENT_VOUCHER_DATA_QUERY_KEY = "payment-voucher-bank-list";
const CLIENT_DDL_DATA_QUERY_KEY = "client-ddl-data-record";
const ACCOUNTANT_DDL_DATA_QUERY_KEY = "accountant-ddl-data-record";
const EMPLOYEE_DDL_DATA_QUERY_KEY = "employee-ddl-data-record";

const INITIAL_TABLE_ROWS = Array.from({ length: 5 }, (_, i) => ({
	id: String(i + 1),
	type: null,
	periodType: null,
	periodDate: null,
	amount: null,
	comments: null,
}));

const INITIAL_PERIOD_TYPE_DDL = [
	{ label: "--", value: null },
	{ label: "Monthly", value: 200 },
	{ label: "Semi Monthly", value: 300 },
	{ label: "BiWeekly", value: 100 },
	{ label: "Weekly", value: 400 },
];

const DEFAULT_FORM_VALUES = {
	transactionType: 200,
	bankName: null,
	financialInstitutionNo: null,
	bankTransitNo: null,
	bankAccountNo: null,
	voucherDate: null,
	accountantId: "all",
	clientId: "all",
	employeeId: "all",
	clientName: null,
};

const useGetDataPaymentVoucherSetupById = (
	id: string | undefined,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [SINGLE_RECORD_DATA_QUERY_KEY, id],
		queryFn: () => {
			return TransactionsServices.getPaymentVoucherData(id, navigateToLogout);
		},
		refetchOnWindowFocus: false,
	});

const useGetEmployeeDDL = (clientId: string) =>
	useQuery({
		queryKey: [EMPLOYEE_DDL_DATA_QUERY_KEY, clientId],
		queryFn: () => {
			return EmployeeService.getEmployeeDDL(clientId);
		},
		refetchOnWindowFocus: false,
	});
const useGetAccountantDDLItemsItems = () =>
	useQuery({
		queryKey: [ACCOUNTANT_DDL_DATA_QUERY_KEY],
		queryFn: (data) => {
			return AccountantService.getGetAccountantDDL();
		},
		refetchOnWindowFocus: false,
	});
const useGetClientsDDLItems = (accountantId: string) =>
	useQuery({
		queryKey: [CLIENT_DDL_DATA_QUERY_KEY, accountantId],
		queryFn: (data) => {
			return ClientService.getGetClientDDL(accountantId);
		},
		refetchOnWindowFocus: false,
	});

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Deep-clone the initial table rows to avoid mutation */
const freshTableRows = () =>
	INITIAL_TABLE_ROWS.map((row) => ({ ...row }));

export const PaymentVoucherSetup: React.FC<IPaymentVoucherSetup> = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const navigateToLogout = useNavigateToLogout();
	const queryClient = useQueryClient();
	const { id } = useParams();

	const clientId = useSelector((state: IAppState) => state.client.id);
	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);
	// ── Local State ─────────────────────────────────────────────────────────────

	const [editRecordId, setEditRecordId] = useState(id);
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const [transactionType, setTransactionType] = useState<any>(200);
	const [accountantId, setAccountantId] = useState<string>("all");
	const [selectedClientId, setSelectedClientId] = useState<string>(clientId);
	const [periodTypeDDL, setPeriodTypeDDL] = useState(INITIAL_PERIOD_TYPE_DDL);
	const [tableDataItems, setTableDataItems] = useState<any[]>(freshTableRows());
	const [tableUpdatedData, setTableUpdatedData] = useState<any[]>(freshTableRows());
	const [sumOfAllAmounts, setSumOfAllAmounts] = useState<number>(0);

	/**
	 * Incrementing this key tells the right-panel DataTable to fully re-mount
	 * and reset all internal state (period-date DDL map, etc.).
	 */
	const [tableResetKey, setTableResetKey] = useState<number>(0);

	const [queryParams, setQueryParams] = useState<IGetListRequest>({
		search_query: "",
		order_by: "name",
		order_direction: "asc",
		page: 1,
		size: 25,
	});

	const onRequestParamsChange = useCallback((requestParams: any) => {
		let dispatchParams = {
			search_query: requestParams.searchQuery ? requestParams.searchQuery : "",
			order_by:
				requestParams.sortField !== null ? requestParams.sortField : "name",
			order_direction: requestParams.sortOrder
				? requestParams.sortOrder
				: "asc",
			page: requestParams.page + 1,
			size: requestParams.rows,
		};

		setQueryParams(dispatchParams);
	}, []);

	useEffect(() => {
		setEditRecordId(id)
	}, [id])

	// const { isPending, data } = useGetEmployeeBankList(
	// 	employeeIdState,
	// 	queryParams,
	// 	navigateToLogout
	// );

	// const employeeBankList: any | null = data;


	const { isPending: isPendingAccountantDDL, data: accountantDDL } =
		useGetAccountantDDLItemsItems();
	const { isPending: isPendingClientsDDL, data: clientsDDL } =
		useGetClientsDDLItems(accountantId);
	const { isPending: isPendingEmployeeDDL, data: EmployeeDDL } = useGetEmployeeDDL(selectedClientId);

	// ── Helpers ──────────────────────────────────────────────────────────────────

	const resetTable = useCallback(() => {
		const fresh = freshTableRows();
		setTableDataItems(fresh);
		setTableUpdatedData(fresh);
		setTableResetKey((k) => k + 1);
	}, []);


	const accountantDDLItems = useMemo(() => {
		if (accountantDDL) {
			if (
				accountantDDL &&
				Array.isArray(accountantDDL)
			) {
				let resources: IDropDownListResponse[] =
					sortDDLByLabel(accountantDDL).map(
						(resourceItem: IDropDownListResponse) => ({
							// id: resourceItem.id,
							label: resourceItem.label,
							value: resourceItem.value,
						})
					);

				resources.unshift({
					label: "All",
					value: "all",
				});

				return resources;
			}
			return [];
		}
	}, [accountantDDL]);

	const clientsDDLItems = useMemo(() => {
		if (clientsDDL) {
			if (
				clientsDDL &&
				Array.isArray(clientsDDL)
			) {
				let resources: IDropDownListResponse[] =
					sortDDLByLabel(clientsDDL).map(
						(resourceItem: IDropDownListResponse) => ({
							// id: resourceItem.id,
							label: resourceItem.label,
							value: resourceItem.value,
						})
					);

				resources.unshift({
					label: "All",
					value: "all",
				});

				return resources;
			}
			return [];
		}
	}, [clientsDDL]);

	const employeeDDLItems = useMemo(() => {
		if (EmployeeDDL) {
			if (
				EmployeeDDL &&
				Array.isArray(EmployeeDDL)
			) {
				let resources: IDropDownListResponse[] =
					sortDDLByLabel(EmployeeDDL).map(
						(resourceItem: IDropDownListResponse) => ({
							// id: resourceItem.id,
							label: resourceItem.label,
							value: resourceItem.value,
						})
					);

				resources.unshift({
					label: "All",
					value: "all",
				});

				return resources;
			}
			return [];
		}
	}, [EmployeeDDL]);

	// ── Mutation ─────────────────────────────────────────────────────────────────

	const { isPending: isSubmitting, mutate: submitForm } = useMutation({
		mutationFn: TransactionsServices.saveAsDraftPaymentVoucher,
		onSuccess: (res: any) => {
			if (res?.status === 200 || res?.status === 201) {
				setEditRecordId(res.data.id);
				queryClient.invalidateQueries({ queryKey: [SINGLE_RECORD_DATA_QUERY_KEY] });
				queryClient.invalidateQueries({ queryKey: [PAYMENT_VOUCHER_DATA_QUERY_KEY] });
				setSnackbarData({
					open: true,
					message: "Data has been saved successfully!",
					severity: "success",
				});
				scrollPanelFieldsToTop();
				navigate(`/voucher/payment/setup/${res.data.id}`);
			} else {
				setSnackbarData({
					open: true,
					message: res.message || "Something went wrong!",
					severity: "error",
				});
			}
		},
		onError: () => {
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});

	const DEFAULT_VALUES = {
		transactionType: 200,
		bankName: null,
		financialInstitutionNo: null,
		bankTransitNo: null,
		bankAccountNo: null,
		voucherDate: null,
		accountantId: "all",
		clientId: "all",
		employeeId: "all",
		clientName: null,
	};

	const ExtendedValidationSchema = PaymentVoucherSetupValidationSchema.shape({
		clientId: yup.string().when("transactionType", (transactionType: any, schema: any) => {
			if (transactionType[0] === 100 || transactionType[0] === 300) {
				return schema.test(
					"client-not-all",
					"Please select a client!",
					(value: any) => value !== "all" && value !== null && value !== undefined
				);
			}
			return schema;
		}),

		employeeId: yup.string().when("transactionType", (transactionType: any, schema: any) => {
			if (transactionType[0] === 300) {
				return schema.test(
					"employee-not-all",
					"Please select an employee!",
					(value: any) => value !== "all" && value !== null && value !== undefined
				);
			}
			return schema;
		}),
	});

	const formMethods = useForm<any>({
		resolver: yupResolver(ExtendedValidationSchema),
		mode: "onBlur",
		reValidateMode: "onChange",
		defaultValues: DEFAULT_VALUES,
	});

	const { data: selectedDataRecordPaymentVoucher } = useGetDataPaymentVoucherSetupById(
		editRecordId,
		navigateToLogout
	);

	// const onSubmit = (values: any) => {
	// 	// const formattedPaymentVoucherTableRowsData =
	// 	// 	paymentVoucherFieldsTableUpdatedData?.map((rows: any) => ({
	// 	// 		...rows,
	// 	// 		periodDate: formatDate(rows.periodDate),
	// 	// 	}));
	// 	const formattedPaymentVoucherTableRowsData =
	// 		paymentVoucherFieldsTableUpdatedData
	// 			?.filter((rows: any) => rows.amount !== null && rows.amount !== undefined && rows.amount !== "")
	// 			.map((rows: any) => ({
	// 				...rows,
	// 				periodDate: formatDate(rows.periodDate),
	// 			}));

	// 	const selectedEmployeeName = EmployeeDDL && EmployeeDDL.find((x: any) => values.employeeId === x.value)?.label;
	// 	const selectedClientName = clientsDDL && clientsDDL.find((x: any) => values.clientId === x.value)?.label;
	// 	const selectedAccountantName = accountantDDL && accountantDDL.find((x: any) => values.accountantId === x.value)?.label;

	// 	let payload = {
	// 		transactionType: values.transactionType,
	// 		bankName: values.bankName,
	// 		financialInstitutionNo: values.financialInstitutionNo,
	// 		bankTransitNo: values.bankTransitNo,
	// 		bankAccountNo: values.bankAccountNo,
	// 		voucherDate: formatDate(values.voucherDate),
	// 		accountantId: values.accountantId,
	// 		clientId: values.clientId,
	// 		employeeId: values.employeeId,
	// 		clientName: values.clientName,
	// 		paymentVoucherTable: formattedPaymentVoucherTableRowsData,
	// 		partyName: values.transactionType === 100 ? selectedClientName : values.transactionType === 300 ? selectedClientName : values.clientName,
	// 		employeeName: values.transactionType === 300 ? selectedEmployeeName : null,
	// 		totalOfAllAmounts: sumOfAllAmounts

	// 	};

	// 	submitPaymentVoucherSaveAsDraftFormFields(payload);



	// };

	// ── Submit ───────────────────────────────────────────────────────────────────

	const onSubmit = (values: any) => {
		const filteredRows = tableUpdatedData
			.filter(
				(row) => row.amount !== null && row.amount !== undefined && row.amount !== ""
			)
			.map((row) => ({ ...row, periodDate: formatDate(row.periodDate) }));

		const selectedEmployee = EmployeeDDL?.find(
			(x: any) => values.employeeId === x.value
		)?.label;
		const selectedClient = clientsDDL?.find(
			(x: any) => values.clientId === x.value
		)?.label;

		submitForm({
			transactionType: values.transactionType,
			bankName: values.bankName,
			financialInstitutionNo: values.financialInstitutionNo,
			bankTransitNo: values.bankTransitNo,
			bankAccountNo: values.bankAccountNo,
			voucherDate: formatDate(values.voucherDate),
			accountantId: values.accountantId,
			clientId: values.clientId,
			employeeId: values.employeeId,
			clientName: values.clientName,
			paymentVoucherTable: filteredRows,
			partyName:
				values.transactionType === 100 || values.transactionType === 300
					? selectedClient
					: values.clientName,
			employeeName:
				values.transactionType === 300 ? selectedEmployee : null,
			totalOfAllAmounts: sumOfAllAmounts,
		});
	};


	useEffect(() => {
		if (id && selectedDataRecordPaymentVoucher) {

			Object.keys(selectedDataRecordPaymentVoucher).forEach((fieldName) => {
				const fieldValue = selectedDataRecordPaymentVoucher[fieldName];
				formMethods.setValue(fieldName, fieldValue ?? undefined);

				if (fieldName === 'clientId') {
					(async () => {
						try {
							const response = await TransactionsServices.getPeriodTypesByClientId(fieldValue)
							setPeriodTypeDDL(response.sheduleDdl);
							// resetTable();

							// setTableResetKey((k) => k + 1);
						} catch {
							setPeriodTypeDDL(INITIAL_PERIOD_TYPE_DDL);
						}
					})();
				}
				if (fieldName === "paymentVoucherTable" && Array.isArray(fieldValue) && fieldValue && fieldValue.length > 0) {
					const formattedPaymentVoucherListData = fieldValue.map((item: any) => ({
						...item,
						// periodDate: formatDate(item.periodDate),
						// periodDate: item.periodDate !== "" && item.periodDate ? new Date(item.periodDate) : item.periodDate,
					}));

					setTableDataItems(formattedPaymentVoucherListData);
					setTableUpdatedData(formattedPaymentVoucherListData);


				} else if (fieldName === "paymentVoucherTable" && Array.isArray(fieldValue) && fieldValue && fieldValue.length === 0) {
					const fresh = freshTableRows();
					setTableDataItems(fresh);
					setTableUpdatedData(fresh);
				}
				if (fieldName === 'transactionType') {
					setTransactionType(fieldValue)
				}

				if (fieldName === 'transactionType' && fieldValue === 100) {
					if (selectedDataRecordPaymentVoucher.accountantId !== "") setAccountantId(selectedDataRecordPaymentVoucher.accountantId)
					setSelectedClientId(selectedDataRecordPaymentVoucher.clientId);
				} else if (fieldName === 'transactionType' && fieldValue === 300) {
					if (selectedDataRecordPaymentVoucher.accountantId !== "") setAccountantId(selectedDataRecordPaymentVoucher.accountantId)
					setSelectedClientId(selectedDataRecordPaymentVoucher.clientId);
				}
				if (fieldName === 'voucherDate') {
					let date = new Date(fieldValue);
					formMethods.setValue(fieldName, date);
				}


			});

		}
	}, [id && selectedDataRecordPaymentVoucher]);


	const onSnackbarClosed = (
		_event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	// const onResetButtonClickedHandler = () => {
	// 	setSnackbarData({
	// 		open: true,
	// 		message: "Success",
	// 		severity: "success",
	// 	});
	// 	formMethods.reset(DEFAULT_VALUES);
	// 	setTransactionType(null);
	// 	setPaymentVoucherFieldsTableUpdatedData(
	// 		paymentVoucherInitialTableData.map((item) => ({ ...item }))
	// 	);
	// 	setPaymentVoucherFieldsDataItems(
	// 		paymentVoucherInitialTableData.map((item) => ({ ...item }))
	// 	);
	// 	let path = `/voucher/payment/setup`;
	// 	navigate(path);
	// };

	// ── Reset ────────────────────────────────────────────────────────────────────

	const onResetButtonClickedHandler = () => {
		formMethods.reset(DEFAULT_FORM_VALUES);
		setTransactionType(200);
		setAccountantId("all");
		setPeriodTypeDDL(INITIAL_PERIOD_TYPE_DDL);
		resetTable();
		setSnackbarData({
			open: true,
			message: "Success",
			severity: "success",
		});
		scrollPanelFieldsToTop();
		navigate("/voucher/payment/setup");
	};

	const expandedLabels = ["Misc.", "Payment Voucher"];
	const expandedPaymentVoucherSetupOrSetupItem = "Setup"; // Special case for "Setup" under "City Tax"
	const enhanceData = (data: any[], parentLabel?: string): any[] => {
		return data.map((item) => {
			let isExpanded = expandedLabels.includes(item.label);
			if (parentLabel === "Payment Voucher" &&
				item.label === expandedPaymentVoucherSetupOrSetupItem) {
				isExpanded = true;
			}
			const enhancedItem = {
				...item,
				expanded: item.items ? isExpanded : false,
				items: item.items ? enhanceData(item.items, item.label) : undefined,
				path: parentLabel === "Payment Voucher" && item.label === expandedPaymentVoucherSetupOrSetupItem && "/voucher/payment/setup",
			};
			return enhancedItem;
		});
	};

	useEffect(() => {
		dispatch(setSelectedStickyNavBar({ isSticky: true }));
		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
	}, [drawerMenuOptions]);


	// const onAccountantSelectionChangeHandler = (event: any) => {
	// 	setRunGetListAPICall(false)
	// 	setAccountantId(event.value)
	// }

	// const onAccountantSelectionChangeHandler = (event: any) => {
	// 	setRunGetListAPICall(false)
	// 	setAccountantId(event.value)
	// 	formMethods.setValue("clientId", undefined);
	// 	formMethods.setValue("employeeId", undefined);
	// }

	// const onClientSelectionChangeHandler = (event: any) => {
	// 	setRunGetListAPICall(false);
	// 	setSelectedClientId(event.value);
	// 	formMethods.setValue("employeeId", undefined);

	// 	// setPaymentVoucherPeriodTypeDDL([
	// 	// 	{ label: "--", value: null },
	// 	// 	{
	// 	// 		label: "Monthly",
	// 	// 		value: 200,
	// 	// 	}
	// 	// ])
	// };

	// ── Handlers ────────────────────────────────────────────────────────────────

	const onTransactionTypeChange = useCallback(
		(value: any) => {
			setTransactionType(value);
			setAccountantId("all");
			setSelectedClientId("");
			// Reset table + period type DDL back to defaults
			formMethods.setValue("bankName", null);
			formMethods.setValue("financialInstitutionNo", null);
			formMethods.setValue("bankTransitNo", null);
			formMethods.setValue("bankAccountNo", null);

			if (value === 200) {
				setPeriodTypeDDL(INITIAL_PERIOD_TYPE_DDL);
			} else {
				setPeriodTypeDDL([{ label: "Load client first", value: null }]);
			}
			resetTable();
		},
		[resetTable]
	);

	const onAccountantSelectionChangeHandler = useCallback(
		(event: any) => {
			setAccountantId(event.value);
			formMethods.setValue("clientId", "all");
			formMethods.setValue("bankName", null);
			formMethods.setValue("financialInstitutionNo", null);
			formMethods.setValue("bankTransitNo", null);
			formMethods.setValue("bankAccountNo", null);
			formMethods.setValue("employeeId", "all");
			// Reset table + period type DDL when accountant changes
			resetTable();
			setPeriodTypeDDL(INITIAL_PERIOD_TYPE_DDL);
		},
		[formMethods, resetTable]
	);

	const onClientSelectionChangeHandler = useCallback(
		async (event: any) => {
			setSelectedClientId(event.value);
			formMethods.setValue("employeeId", "all");

			// Fetch period-type options for this client
			try {
				// Replace with real API: TransactionsServices.getPeriodTypesByClientId(event.value)
				const response = await TransactionsServices.getPeriodTypesByClientId(event.value)
				// const response: { label: string; value: any }[] = [
				// 	{ label: "--", value: null },
				// 	{ label: "Monthly", value: 200 },
				// ];
				setPeriodTypeDDL(response.sheduleDdl);
				if (response.clientBankDetail) {
					formMethods.setValue("bankName", response.clientBankDetail.name);
					formMethods.setValue("financialInstitutionNo", response.clientBankDetail.bankFinancialNo);
					formMethods.setValue("bankTransitNo", response.clientBankDetail.branchTransitNumber);
					formMethods.setValue("bankAccountNo", response.clientBankDetail.accountNo);
				}
				// Reset table when client changes
				resetTable();
			} catch {
				setPeriodTypeDDL(INITIAL_PERIOD_TYPE_DDL);
			}
		},
		[formMethods, resetTable]
	);

	const onSaveAndPostButtonClickedHandler = () => {
		alert("cliecked")
	}

	// ── Render ───────────────────────────────────────────────────────────────────

	const isLoading =
		isSubmitting ||
		isPendingAccountantDDL ||
		isPendingClientsDDL ||
		isPendingEmployeeDDL;

	return (
		<>
			<div
				style={{
					margin: "15px 0",
					fontSize: "1.5rem",
					fontWeight: "bold",
					fontFamily: "'Roboto','Helvetica','Arial',sans-serif",
				}}
			>
				{"Payment Voucher Setup"}
			</div>

			<Grid>
				<BackdropCircularProgress
					open={isLoading}
					testId="payment voucher Setup"
				/>
				<FormProvider {...formMethods}>
					<form onSubmit={formMethods.handleSubmit(onSubmit)}>
						<Grid container size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
							<Grid size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
								<SingleColumnLayout
									children={
										<StandardPanel
											title="Payment Voucher Setup"
											testId={`payment`}
											// leftSpacing={1}
											// rightSpacing={1}
											showTitle={false}
											variant="outlined"
										// topPadding={2}
										>
											<PaymentVoucherSetupFeildsPanel
												testId={"payment-voucher-fields"}
												// setStateRunGetListAPICall={(value: boolean) => setRunGetListAPICall(value)}
												clientsDDL={clientsDDLItems}
												accountantDDL={accountantDDLItems}
												employeeDDL={employeeDDLItems}
												onAccountantSelectionChangeHandler={onAccountantSelectionChangeHandler}
												onClientSelectionChangeHandler={onClientSelectionChangeHandler}
												transactionType={transactionType}
												// setTransactionType={setTransactionType}
												setTransactionType={onTransactionTypeChange}
												formMethods={formMethods}

											/>
										</StandardPanel>
									}
									toolbar={
										<PaymentVoucherSetupToolbar
											// onSaveAsDraftButtonClickedHandler={onSaveAsDraftButtonClickedHandler}
											onSaveAndPostButtonClickedHandler={onSaveAndPostButtonClickedHandler}
											onResetButtonClickedHandler={onResetButtonClickedHandler}
										/>
									}
									showFullDiaplay={true}
									toolbarPosition="bottom"
									testId="form-layout"
								/>
							</Grid>
							<Grid size={{ xs: 0, sm: 0, md: 0.5, lg: 0.5, xl: 0.5 }}></Grid>

							<Grid
								size={{ xs: 12, sm: 12, md: 7.5, lg: 7.5, xl: 7.5 }}
							>
								<PaymentVoucherSetupRightTablePanel
									paymentVoucherFieldsTableUpdatedData={(data: any) =>
										setTableUpdatedData(data)
									}
									paymentVoucherFieldsDataItems={
										tableDataItems
									}
									sumOfAllAmounts={(value: any) => setSumOfAllAmounts(value)}
									// PaymentVoucherPeriodTypeDDL={PaymentVoucherPeriodTypeDDL}
									transactionType={transactionType}
									PaymentVoucherPeriodTypeDDL={periodTypeDDL}
									resetKey={tableResetKey}

								/>
							</Grid>

						</Grid>
					</form>
				</FormProvider>
				{/* <EnhancedDialog
					dialogTitle={"Please Confirm"}
					key="dialog-box"
					testId="dialog"
					onCloseHandler={onCloseHandler}
					open={openDialog}
					dialogContent={dialogContent}
					showdividers={false}
					dialogAction={
						<Grid>
							<EnhancedButtonPrimeReact
								label="Cancel"
								severity="secondary"
								text
								raised
								onClickHandler={onCloseHandler}
								style={{ fontSize: "small" }}
							/>
							<EnhancedButtonPrimeReact
								label={dialogButtonLabel}
								severity={
									buttonTag === 'save-as-draft' ? EnhancedButtonPrimeReactSeverity.Success :
										buttonTag === 'save-and-post' ? EnhancedButtonPrimeReactSeverity.Success :
											EnhancedButtonPrimeReactSeverity.Secondary}
								raised
								onClickHandler={() => dialogButtonClickedHandler(buttonTag)}
								style={{ marginLeft: "16px", fontSize: "small" }}
							/>
						</Grid>
					}
				/> */}

				<EnhancedSnackbar
					message={snackbarData.message}
					onCloseHandler={onSnackbarClosed}
					severity={snackbarData.severity}
					testId={"employee-snackbar"}
					open={snackbarData.open}
				/>
			</Grid>
		</>
	);
};
